import {
  DetailsList,
  Link,
  Panel,
  PanelType,
  SelectionMode,
} from '@fluentui/react';
import { IPagedCollection } from '../../services/assetServices';
import { getWorks, IWork } from '../../services/workService';
import { useCallback, useEffect, useState } from 'react';
import {
  TenantSelectorField,
  pageTenants,
  useSearchInput,
  type GetTenantsFn,
} from '@moko/ui';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
} from '../../atoms/messageBarAtoms';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  Field,
  FluentProvider,
  Input,
  webLightTheme,
} from '@fluentui/react-components';

export interface IWorkSelectorProps {
  isOpen: boolean;
  hideWorkSelector: any;
  onWorkSelected: (id: number, work?: IWork) => void;
}

const WorkSelector = (props: IWorkSelectorProps) => {
  const [works, setWorks] = useState<IWork[]>([]);
  const [searchText, setSearchText] = useState('');
  const profileData = useAtomValue(profileDataAtom);
  const [selectedTenantId, setSelectedTenantId] = useState<number | undefined>(
    profileData.currentTenant || undefined
  );

  const getTenants: GetTenantsFn = useCallback(
    async (_ac, pageSize, pageNo, _orderBy, search) =>
      pageTenants(
        (profileData.tenants || []).map((tenant) => ({
          id: tenant.id,
          name: tenant.name,
        })),
        pageSize,
        pageNo,
        search
      ),
    [profileData.tenants]
  );

  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const getTenantFilter = (tenantId?: number) => {
    if (!tenantId) {
      return undefined;
    }

    return JSON.stringify({
      filters: [
        {
          column: 'tenantId',
          operation: 'Eq',
          value: `${tenantId}`,
          type: 'Number',
        },
      ],
    });
  };

  const fetchData = async (search?: string, tenantId?: number) => {
    const abortController = new AbortController();
    setIsInProgress(true);
    try {
      const works: IPagedCollection<IWork> = await getWorks(
        abortController,
        10,
        1,
        undefined,
        search,
        getTenantFilter(tenantId)
      );
      setWorks(works.items);
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    if (props.isOpen) {
      const tenantId = selectedTenantId ?? profileData.currentTenant;
      if (!selectedTenantId && tenantId) {
        setSelectedTenantId(tenantId);
      }
      fetchData(undefined, tenantId || undefined);
    }
  }, [props.isOpen, profileData.currentTenant, selectedTenantId]);

  const { getInputProps } = useSearchInput({
    value: searchText,
    onChange: setSearchText,
    onSearch: (term) => fetchData(term || undefined, selectedTenantId),
  });
  const searchInputProps = getInputProps();

  return (
    <Panel
      isLightDismiss
      isOpen={props.isOpen}
      onDismiss={props.hideWorkSelector}
      closeButtonAriaLabel="Close"
      type={PanelType.medium}
      headerText="Pick work"
    >
      <FluentProvider theme={webLightTheme}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <TenantSelectorField
            getTenants={getTenants}
            tenantId={selectedTenantId}
            tenantName={
              profileData.tenants?.find(
                (tenant) => tenant.id === selectedTenantId
              )?.name
            }
            onSelected={(tenant) => {
              setSelectedTenantId(tenant.id);
              fetchData(searchText || undefined, tenant.id);
            }}
          />
          <Field label="Search">
            <Input
              type="search"
              value={searchInputProps.value}
              placeholder="Search work"
              onChange={searchInputProps.onChange}
              onKeyDown={searchInputProps.onKeyDown}
            />
          </Field>
        </div>
      </FluentProvider>
      <DetailsList
        columns={[
          {
            key: 'Id',
            name: 'Id',
            fieldName: 'id',
            minWidth: 50,
            maxWidth: 50,
            isResizable: true,
            onRender: (item) => (
              <Link
                key={item.id}
                onClick={() => {
                  props.onWorkSelected(item?.id, item as IWork);
                }}
              >
                {item.id}
              </Link>
            ),
          },
          {
            key: 'Code',
            name: 'Code',
            fieldName: 'code',
            minWidth: 75,
            maxWidth: 100,
            isResizable: true,
          },
          {
            key: 'Status',
            name: 'Status',
            fieldName: 'status',
            minWidth: 75,
            maxWidth: 100,
            isResizable: true,
          },
          {
            key: 'StatusDescription',
            name: 'StatusDescription',
            fieldName: 'statusDescription',
            minWidth: 150,
            maxWidth: 175,
            isResizable: true,
          },
        ]}
        compact={true}
        items={works}
        selectionMode={SelectionMode.none}
      />
    </Panel>
  );
};

export default WorkSelector;
